import React, { useState, useRef, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { FaCalendarAlt, FaTimes } from 'react-icons/fa'
import moment from 'moment'
import './CustomDatePicker.scss'

const CustomDatePicker = ({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  className = "",
  error = false,
  label,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value || "")
  const [currentMonth, setCurrentMonth] = useState(moment(value || undefined))
  const [dropdownPosition, setDropdownPosition] = useState('left')
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    setInputValue(value || "")
    if (value && moment(value, "DD/MM/YYYY", true).isValid()) {
      setCurrentMonth(moment(value, "DD/MM/YYYY"))
    }
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      calculateDropdownPosition()
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const calculateDropdownPosition = () => {
    if (!inputRef.current) return

    const inputRect = inputRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const dropdownWidth = 300

    if (inputRect.left + dropdownWidth > viewportWidth) {
      if (inputRect.right - dropdownWidth < 0) {
        setDropdownPosition('center')
      } else {
        setDropdownPosition('right')
      }
    } else {
      setDropdownPosition('left')
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)

    // Auto-format as user types
    const cleanValue = newValue.replace(/\D/g, '')
    let formattedValue = cleanValue

    if (cleanValue.length >= 2) {
      formattedValue = cleanValue.substring(0, 2) + '/' + cleanValue.substring(2)
    }
    if (cleanValue.length >= 4) {
      formattedValue = cleanValue.substring(0, 2) + '/' + cleanValue.substring(2, 4) + '/' + cleanValue.substring(4, 8)
    }

    if (formattedValue !== newValue) {
      setInputValue(formattedValue)
    }

    // Validate and call onChange if valid
    if (moment(formattedValue, "DD/MM/YYYY", true).isValid()) {
      onChange(formattedValue)
      setCurrentMonth(moment(formattedValue, "DD/MM/YYYY"))
    } else if (formattedValue === "") {
      onChange("")
    }
  }

  const handleDateClick = (date, isCurrentMonth = true) => {
    // If clicking a date from adjacent month, navigate to that month first
    if (!isCurrentMonth) {
      setCurrentMonth(moment(date))
      return
    }

    const formattedDate = moment(date).format("DD/MM/YYYY")
    setInputValue(formattedDate)
    onChange(formattedDate)
    setIsOpen(false)
  }

  const handleToday = () => {
    const today = moment().format("DD/MM/YYYY")
    setInputValue(today)
    onChange(today)
    setIsOpen(false)
  }

  const handleClear = () => {
    setInputValue("")
    onChange("")
    setIsOpen(false)
  }

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => moment(prev).add(direction, 'month'))
  }

  const navigateYear = (direction) => {
    setCurrentMonth(prev => moment(prev).add(direction, 'year'))
  }

  const renderCalendar = () => {
    const startOfMonth = moment(currentMonth).startOf('month')
    const startOfCalendar = moment(startOfMonth).startOf('isoWeek') // isoWeek starts on Monday
    const today = moment()
    const selectedDate = inputValue && moment(inputValue, "DD/MM/YYYY", true).isValid()
      ? moment(inputValue, "DD/MM/YYYY")
      : null

    const weeks = []
    let currentWeek = []

    for (let i = 0; i < 42; i++) {
      const day = moment(startOfCalendar).add(i, 'days')
      const isCurrentMonth = day.month() === currentMonth.month()
      const isToday = day.isSame(today, 'day')
      const isSelected = selectedDate && day.isSame(selectedDate, 'day')

      currentWeek.push(
        <div
          key={day.format('YYYY-MM-DD')}
          className={`calendar-day ${isCurrentMonth ? 'current-month' : 'other-month'}
                     ${isToday ? 'today' : ''}
                     ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(day.toDate(), isCurrentMonth)}
          title={!isCurrentMonth ? `Click to go to ${day.format('MMMM YYYY')}` : ''}
        >
          {day.date()}
        </div>
      )

      if ((i + 1) % 7 === 0) {
        weeks.push(
          <div key={`week-${weeks.length}`} className="calendar-week">
            {currentWeek}
          </div>
        )
        currentWeek = []
      }
    }

    return (
      <div className="calendar">
        <div className="calendar-header">
          <div className="year-nav">
            <Button variant="link" size="sm" onClick={() => navigateYear(-1)}>‹‹</Button>
            <span className="year">{currentMonth.format('YYYY')}</span>
            <Button variant="link" size="sm" onClick={() => navigateYear(1)}>››</Button>
          </div>
          <div className="month-nav">
            <Button variant="link" size="sm" onClick={() => navigateMonth(-1)}>‹</Button>
            <span className="month">{currentMonth.format('MMMM')}</span>
            <Button variant="link" size="sm" onClick={() => navigateMonth(1)}>›</Button>
          </div>
        </div>
        <div className="calendar-weekdays">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        <div className="calendar-body">
          {weeks}
        </div>
      </div>
    )
  }

  const isValid = !inputValue || moment(inputValue, "DD/MM/YYYY", true).isValid()

  return (
    <div className={`custom-date-picker ${className}`} ref={dropdownRef}>
      {label && (
        <label className="form-label">
          {label}
        </label>
      )}
      <div className="picker-input-container">
        <div
          className={`picker-input ${isOpen ? 'open' : ''} ${error || !isValid ? 'error' : ''}`}
          ref={inputRef}
        >
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="date-input"
            maxLength={10}
            onFocus={() => setIsOpen(true)}
          />
          <div className="input-icons">
            {inputValue && (
              <FaTimes
                className="clear-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
              />
            )}
            <FaCalendarAlt
              className="calendar-icon"
              onClick={() => setIsOpen(!isOpen)}
            />
          </div>
        </div>

        {!isValid && inputValue && (
          <div className="invalid-feedback">Please enter a valid date (DD/MM/YYYY)</div>
        )}
      </div>

      {isOpen && (
        <div className={`picker-dropdown ${dropdownPosition === 'right' ? 'align-right' : dropdownPosition === 'center' ? 'align-center' : ''}`}>
          <div className="shortcuts">
            <Button variant="outline-primary" size="sm" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>
              Clear
            </Button>
          </div>

          {renderCalendar()}
        </div>
      )}
    </div>
  )
}

export default CustomDatePicker